import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
// Importing this installs the axios interceptors that carry the session cookie, the
// portal name and the CSRF token on every request. See ../session.js.
import { clearSession, revokeSession } from '../session';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AuthContext = createContext();

// HIPAA: automatic logoff after inactivity (4 hours)
const IDLE_TIMEOUT_MS = 240 * 60 * 1000;

export function useAuth() {
  return useContext(AuthContext);
}

// The server refuses every other call with this 403 while an administrator-set
// password is still in place (patients don't normally get one, but an identity that
// is also clinic staff can).
const MUST_CHANGE_PASSWORD_RE = /must change the password/i;

// GET /auth/me is how a reload finds out whether there is a session at all, so its own
// 401 is an answer rather than an error — it must not bounce the browser to /login.
const AUTH_ME_RE = /\/auth\/me$/;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // True until the server has answered who we are. Without it the first render sends a
  // signed-in patient to /login and the route they asked for is lost.
  const [loading, setLoading] = useState(true);
  // Kept apart from `user` because `user` is replaced wholesale whenever the server
  // sends a fresh profile, and not every one of those carries this flag.
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const logoutRef = useRef(null);
  const idleTimerRef = useRef(null);

  const logout = useCallback(() => {
    const revoked = revokeSession();
    clearSession();
    setUser(null);
    setIsAuthenticated(false);
    setMustChangePassword(false);
    return revoked;
  }, []);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  /** Adopt the profile the server just sent as the signed-in patient. */
  // Counts every session this page adopts, so a slow answer about an older one
  // can tell it has been overtaken.
  const sessionGenRef = useRef(0);
  const loginWithToken = useCallback((userData) => {
    if (!userData) return;
    sessionGenRef.current += 1;
    setUser(userData);
    setMustChangePassword(!!userData.must_change_password);
    setIsAuthenticated(true);
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        // Mask unexpected/server errors (5xx, network) with a friendly message;
        // keep actionable 4xx messages (validation, auth, conflicts) intact.
        const st = err?.response?.status;
        if (!err?.response || st >= 500) {
          const FRIENDLY = 'Something went wrong. Our team is looking into it.';
          if (!err.response) err.response = { status: 0, data: {} };
          if (!err.response.data || typeof err.response.data !== 'object') err.response.data = {};
          const eid = err.response.data.error_id;
          err.response.data.detail = eid ? `${FRIENDLY} (ref: ${eid})` : FRIENDLY;
          err.userMessage = err.response.data.detail;
        }
        const url = String(err?.config?.url || '');
        const detail = err?.response?.data?.detail;
        if (st === 403 && typeof detail === 'string' && MUST_CHANGE_PASSWORD_RE.test(detail)) {
          setMustChangePassword(true);
          return Promise.reject(err);
        }
        const isAuthLogin =
          url.includes('/auth/login') || url.includes('/auth/token') || url.includes('/auth/register');
        const isMe = AUTH_ME_RE.test(url.split('?')[0]);
        if (err?.response?.status === 401 && !isAuthLogin && !isMe) {
          logoutRef.current?.();
          window.location.href = '/login';
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Boot and every reload start here. The session is in a cookie this page cannot read,
  // so the server is the only thing that knows whether there is one: 200 means signed
  // in and carries the profile and the CSRF token; 401 means signed out.
  useEffect(() => {
    // The /switch page makes its own session, by redeeming a handoff code — and that
    // redeem REPLACES this portal's cookie. Asking /auth/me here too would send the old
    // cookie in a race with it: when the old answer landed second, the portal showed the
    // role you had just left (its menu, its Administrators item) over a session that was
    // already the new one, and every click on that menu came back 403. So on /switch the
    // redeem is the only thing that decides who you are.
    if (window.location.pathname === '/switch') {
      setLoading(false);
      return undefined;
    }
    const gen = sessionGenRef.current;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/v1/auth/me`)
      .then((res) => {
        // Anything applied since this request left (a switch, a sign-in) is newer than
        // what the cookie said when it was sent; the late answer must not overwrite it.
        if (!cancelled && sessionGenRef.current === gen) loginWithToken(res.data);
      })
      .catch(() => {
        if (!cancelled && sessionGenRef.current === gen) {
          setUser(null);
          setIsAuthenticated(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [loginWithToken]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => logoutRef.current?.(), IDLE_TIMEOUT_MS);
    };
    resetIdleTimer();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer));
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
    };
  }, [isAuthenticated]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/v1/auth/login/patient`, {
      email,
      password
    });

    const { user: userData } = response.data;
    loginWithToken(userData);

    return userData;
  };

  /**
   * After POST /auth/me/password succeeds. The change signs out every earlier token
   * (including the one that made the request) and issues a new one in the cookie, so
   * adopt the profile and CSRF token that came back with it.
   */
  const completePasswordChange = useCallback((data) => {
    if (data?.user) {
      loginWithToken(data.user);
    }
    setMustChangePassword(!!data?.user?.must_change_password);
  }, [loginWithToken]);

  // Unified sign-in: authenticate once, land in the identity's home portal. If that's
  // THIS (patient) portal, sign in here; otherwise hand off to the right portal (e.g. a
  // provider who typed their credentials here). Returns { routed }.
  const unifiedLogin = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/v1/auth/login`, { email, password });
    const { user: userData, default: home, access_token: handoffAuth } = res.data;
    // Only hand off to the clinic portal; anything else signs in locally here.
    if (home && home.portal === 'clinic') {
      // The one place this portal touches the token. The identity's home is the
      // clinic portal, so the server deliberately set no cookie here — it will not
      // leave a clinic session sitting in the patient portal's cookie. That makes this
      // call the exception: it authenticates with the token from the response, which is
      // used once, never stored, and dropped when this function returns.
      const handoff = await axios.post(
        `${API_URL}/api/v1/auth/switch-portal`,
        { role: home.role, clinic_id: home.clinic_id || null },
        { headers: { Authorization: `Bearer ${handoffAuth}` } },
      );
      window.location.assign(handoff.data.handoff_url);
      return { routed: true };
    }
    loginWithToken(userData);
    return { routed: false };
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    unifiedLogin,
    loginWithToken,
    logout,
    mustChangePassword,
    completePasswordChange,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
